import Link from 'next/link';
import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Heading,
  Stack,
  Text
} from '@chakra-ui/react';
import { openPopupWidget } from 'react-calendly';
import { URLS } from '@lib';
import { useSegment } from '@hooks/use-segment';

type Props = {
  badgeText: string;
  description: React.ReactNode;
  featuresTitle?: string;
  features: string[];
  price?: string;
};

function TalkToAnExpertCTA(): JSX.Element {
  const analytics = useSegment();
  return (
    <Button
      onClick={() => {
        openPopupWidget({
          url: URLS.calendlyUrl
        });
        analytics.track('Marketing_Pricing_Talk_To_An_Expert Button Clicked', {
          ClickedOn: 'Enterprise',
          surface: 'Marketing',
          location: 'Pricing'
        });
      }}
      fontSize="14px"
      letterSpacing={'-0.02'}
      lineHeight="24px"
      boxShadow={'0px 4px 8px -2px rgba(57, 67, 86, 0.12)'}
      borderRadius="8px"
      padding="8px 20px"
    >
      Talk with an Expert
    </Button>
  );
}

function StartBuildingNowCTA({ section }: { section: string }): JSX.Element {
  const analytics = useSegment();
  return (
    <Link
      legacyBehavior
      href={process.env.NEXT_PUBLIC_DASHBOARD_URL + '/sign-up'}
      passHref
    >
      <Button
        h="40px"
        padding="8px 20px"
        pos="relative"
        top={0}
        border={section == 'Business' ? '1px' : '0px'}
        borderColor={section == 'Business' ? 'primary.500' : 'none'}
        variant="white"
        zIndex={100}
        onClick={() => {
          analytics.track('Marketing_Pricing_Start Building Button Clicked', {
            ClickedOn: section,
            surface: 'Marketing',
            location: 'Pricing'
          });
        }}
      >
        Start Building Now
      </Button>
    </Link>
  );
}

function MostPopularBadge(): JSX.Element {
  return (
    <Box
      pos="absolute"
      display={'flex'}
      flexDir="column"
      alignItems="center"
      right={2}
      top={-8}
      zIndex={1000}
    >
      <Heading
        fontWeight={900}
        letterSpacing="0.12em"
        fontSize="8px"
        lineHeight={'24px'}
        bgGradient="linear-gradient(271.19deg, #17CCFC -28.89%, #6C47FF 80.53%)"
        bgClip="text"
      >
        MOST POPULAR!
      </Heading>
      <svg
        width="41"
        height="48"
        viewBox="0 0 41 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2.42195 0.900879H38.5794C39.1787 0.900879 39.7535 1.13897 40.1773 1.56277C40.6011 1.98657 40.8392 2.56137 40.8392 3.16072V46.7213C40.8394 46.9099 40.7924 47.0955 40.7025 47.2612C40.6126 47.427 40.4826 47.5676 40.3245 47.6702C40.1663 47.7729 39.985 47.8344 39.797 47.849C39.6091 47.8637 39.4204 47.831 39.2483 47.7541L20.5007 39.3859L1.75304 47.7518C1.58107 47.8287 1.39262 47.8613 1.20482 47.8468C1.01703 47.8323 0.835842 47.771 0.677751 47.6686C0.51966 47.5662 0.389675 47.4259 0.29962 47.2605C0.209564 47.095 0.162294 46.9097 0.162109 46.7213V3.16072C0.162109 2.56137 0.400199 1.98657 0.824 1.56277C1.2478 1.13897 1.8226 0.900879 2.42195 0.900879ZM20.5007 26.889L27.1423 30.3805L25.8745 22.9863L31.2462 17.748L23.8204 16.6678L20.5007 9.94023L17.1787 16.6678L9.75512 17.748L15.1268 22.9863L13.8612 30.3805L20.5007 26.889Z"
          fill="url(#paint0_linear_321_5449)"
        />
        <defs>
          <linearGradient
            id="paint0_linear_321_5449"
            x1="41.0005"
            y1="48"
            x2="0.000486167"
            y2="1"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#17CCFC" />
            <stop offset="1" stopColor="#6C47FF" />
          </linearGradient>
        </defs>
      </svg>
    </Box>
  );
}

export function Card({
  badgeText,
  description,
  featuresTitle,
  features,
  price
}: Props): JSX.Element {
  return (
    <Flex
      paddingBottom={6}
      h="100%"
      textAlign="center"
      flexDir="row"
      justifyContent="center"
      shadow="dropShadow3"
      pos="relative"
      bgGradient={
        badgeText == 'Business'
          ? 'linear(to-br, #E5F8FF 0%, white 70%,  #EAEAFD 100%)'
          : badgeText == 'Hobby'
          ? 'linear(to-bl, #E5F8FF 0%, white 20%,  #EAEAFD 100%)'
          : badgeText == 'Free'
          ? 'linear(to-bl, #E5F8FF 0%, white 70%,  #EAEAFD 100%)'
          : 'linear(to-tr, #E5F8FF 0%, white 80%,  #EAEAFD 100%)'
      }
      borderRadius="8px"
      // overflowX={'hidden'}
    >
      {badgeText === 'Business' && <MostPopularBadge />}
      <Flex align="center" direction="column" borderRadius="8px">
        <Center
          px={'24px'}
          py={'4px'}
          rounded="full"
          mt={'36px'}
          mb="24px"
          border="1px solid #6C47FF"
        >
          <Heading as="h3" size="md" color={'primary.500'}>
            {badgeText}
          </Heading>
        </Center>

        <Flex gap={2} align="flex-end" mb={2} pos="relative">
          <Text
            fontSize={'40px'}
            lineHeight="48px"
            color="primary.500"
            fontWeight={500}
            letterSpacing="-0.01em"
          >
            {price}
          </Text>
          {price !== '$0' && price !== 'Contact Us' && (
            <Text
              fontSize={'32px'}
              lineHeight="48px"
              color="primary.500"
              fontWeight={500}
              letterSpacing="-0.01em"
              ml="-.25em"
            >
              /mo
            </Text>
          )}
        </Flex>

        <Text textStyle="paragraph-sm" color={'black'} mb={4} px={4}>
          {description}
        </Text>
        {badgeText === 'Enterprise' ? (
          <TalkToAnExpertCTA />
        ) : (
          <StartBuildingNowCTA section={badgeText} />
        )}

        <Divider my="24px" />
        {featuresTitle && (
          <Heading size="sm" color={'black'} mb={6}>
            {featuresTitle}
          </Heading>
        )}

        <Stack color={'black'} gap={'12px'}>
          {features.map(feature => (
            <Text key={feature} textStyle="paragraph-sm">
              {feature}
            </Text>
          ))}
        </Stack>
      </Flex>
    </Flex>
  );
}
